import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useBusiness } from '@/providers/business-provider';
import { Sidebar } from '@/components/ui/sidebar';
import { 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Bell, 
  Mail, 
  MessageSquare, 
  FileText,
  DollarSign,
  Building2,
  Settings
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

interface TaxDeadline {
  id: number;
  businessId: number;
  name: string;
  description: string;
  dueDate: string;
  category: string;
  forms: string[];
  priority: string;
  jurisdiction: string;
  isRecurring: boolean;
  status: string;
  estimatedAmount?: string;
  completedAt?: string;
}

interface TaxReminder {
  id: number;
  deadlineId: number;
  businessId: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  daysBefore: number;
  scheduledFor: string;
  sentAt?: string;
  status: string;
  channels: string[];
  actionItems: string[];
  isRead: boolean;
  deadline: TaxDeadline;
  business: { name: string };
}

const formatCurrency = (amount: string | number) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'default';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'outline';
    default:
      return 'secondary';
  }
};

const getReminderTypeColor = (type: string) => {
  switch (type.toLowerCase()) {
    case 'urgent':
      return 'destructive';
    case 'warning':
      return 'default';
    case 'notice':
      return 'secondary';
    case 'early_warning':
      return 'outline';
    default:
      return 'secondary';
  }
};

const getDaysUntilDeadline = (dueDate: string) => {
  const today = new Date();
  const deadline = new Date(dueDate);
  const diffTime = deadline.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export default function TaxReminders() {
  const { activeBusiness, businesses } = useBusiness();
  const { toast } = useToast();
  const [deadlines, setDeadlines] = useState<TaxDeadline[]>([]);
  const [reminders, setReminders] = useState<TaxReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    emailEnabled: true,
    smsEnabled: false,
    appEnabled: true,
  });

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchDeadlines();
      fetchReminders();
    }
  }, [activeBusiness]);

  const fetchDeadlines = async () => {
    try {
      console.log('🔥 Calling emergency tax deadlines endpoint');
      // Use direct fetch to bypass any apiRequest issues
      const response = await fetch(`/api/emergency-tax-deadlines/${activeBusiness?.id}`, {
        credentials: 'include'
      });
      
      console.log('🔥 Raw response status:', response.status);
      console.log('🔥 Raw response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🔥 Parsed JSON data:', data);
      console.log('🔥 Data type:', typeof data);
      console.log('🔥 Is array:', Array.isArray(data));
      
      // Handle the response properly and remove duplicates
      const deadlinesData = Array.isArray(data) ? data : [];
      
      // Remove duplicates based on name, dueDate, and businessId
      const uniqueDeadlines = deadlinesData.filter((deadline, index, self) => 
        index === self.findIndex(d => 
          d.name === deadline.name && 
          d.dueDate === deadline.dueDate && 
          d.businessId === deadline.businessId
        )
      );
      
      console.log('🔥 Original deadlines count:', deadlinesData.length);
      console.log('🔥 Unique deadlines count:', uniqueDeadlines.length);
      console.log('🔥 Setting unique deadlines:', uniqueDeadlines);
      setDeadlines(uniqueDeadlines);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
      setDeadlines([]);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await apiRequest('GET', '/api/tax-reminders/pending');
      setReminders(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeDeadlines = async () => {
    if (!activeBusiness) return;
    
    try {
      setLoading(true);
      await apiRequest('POST', `/api/businesses/${activeBusiness.id}/tax-deadlines/initialize`, {
        businessType: activeBusiness.industry || 'general',
        state: 'california' // You can make this dynamic based on business location
      });
      
      toast({
        title: 'Success',
        description: 'Tax deadlines have been initialized for your business',
      });
      
      fetchDeadlines();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initialize tax deadlines',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const markDeadlineCompleted = async (deadlineId: number) => {
    try {
      await apiRequest('PATCH', `/api/tax-deadlines/${deadlineId}/complete`, {
        completedDate: new Date().toISOString()
      });
      
      toast({
        title: 'Success',
        description: 'Deadline marked as completed',
      });
      
      fetchDeadlines();
      fetchReminders();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to mark deadline as completed',
        variant: 'destructive',
      });
    }
  };

  const markReminderRead = async (reminderId: number) => {
    try {
      await apiRequest('PATCH', `/api/tax-reminders/${reminderId}/read`);
      fetchReminders();
    } catch (error) {
      console.error('Error marking reminder as read:', error);
    }
  };

  const updatePreferences = async () => {
    try {
      await apiRequest('PATCH', '/api/users/reminder-preferences', preferences);
      toast({
        title: 'Success',
        description: 'Reminder preferences updated',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update preferences',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      
      <div className="flex-1 overflow-auto pl-64">
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Smart Tax Reminders
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Stay compliant with automated tax deadline tracking and intelligent reminders
            </p>
          </div>

          <Tabs defaultValue="deadlines" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="deadlines">Tax Deadlines</TabsTrigger>
              <TabsTrigger value="reminders">Active Reminders</TabsTrigger>
              <TabsTrigger value="settings">Preferences</TabsTrigger>
            </TabsList>

            <TabsContent value="deadlines" className="space-y-6">
              {deadlines.length === 0 ? (
                <Card>
                  <CardHeader className="text-center">
                    <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <CardTitle>No Tax Deadlines Found</CardTitle>
                    <CardDescription>
                      Initialize your business tax deadlines to start receiving smart reminders
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <Button onClick={initializeDeadlines}>
                      <Calendar className="mr-2 h-4 w-4" />
                      Initialize Tax Deadlines
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="grid gap-6">
                    {deadlines.map((deadline) => {
                      const daysUntil = getDaysUntilDeadline(deadline.dueDate);
                      const isOverdue = daysUntil < 0 && deadline.status !== 'completed';
                      const isUrgent = daysUntil <= 7 && daysUntil >= 0 && deadline.status !== 'completed';
                      
                      return (
                        <Card key={deadline.id} className={`${isOverdue ? 'border-red-500' : isUrgent ? 'border-orange-500' : ''}`}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <CardTitle className="text-lg">{deadline.name}</CardTitle>
                                  <Badge variant={getPriorityColor(deadline.priority)}>
                                    {deadline.priority}
                                  </Badge>
                                  {deadline.status === 'completed' && (
                                    <Badge variant="default" className="bg-green-500">
                                      <CheckCircle className="mr-1 h-3 w-3" />
                                      Completed
                                    </Badge>
                                  )}
                                </div>
                                <CardDescription>{deadline.description}</CardDescription>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatDate(deadline.dueDate)}
                                </div>
                                <div className={`text-sm font-medium ${isOverdue ? 'text-red-500' : isUrgent ? 'text-orange-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                  {deadline.status === 'completed' ? 'Completed' :
                                   isOverdue ? `${Math.abs(daysUntil)} days overdue` : 
                                   daysUntil === 0 ? 'Due today' :
                                   `${daysUntil} days left`}
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-gray-500" />
                                  <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {deadline.jurisdiction}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-gray-500" />
                                  <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {deadline.forms?.join(', ') || 'No forms specified'}
                                  </span>
                                </div>
                                {deadline.estimatedAmount && (
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                      Est. {formatCurrency(deadline.estimatedAmount)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {deadline.status !== 'completed' && (
                                <div className="flex gap-2">
                                  <Button 
                                    onClick={() => markDeadlineCompleted(deadline.id)}
                                    size="sm"
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Mark Completed
                                  </Button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="reminders" className="space-y-6">
              {reminders.length === 0 ? (
                <Card>
                  <CardHeader className="text-center">
                    <Bell className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <CardTitle>No Active Reminders</CardTitle>
                    <CardDescription>
                      You're all caught up! New reminders will appear here as deadlines approach.
                    </CardDescription>
                  </CardHeader>
                </Card>
              ) : (
                <div className="space-y-4">
                  {reminders.map((reminder) => (
                    <Card key={reminder.id} className={`${!reminder.isRead ? 'ring-2 ring-blue-500' : ''}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <CardTitle className="text-lg">{reminder.title}</CardTitle>
                              <Badge variant={getReminderTypeColor(reminder.type)}>
                                {reminder.type.replace('_', ' ')}
                              </Badge>
                              {!reminder.isRead && (
                                <Badge variant="default" className="bg-blue-500">
                                  New
                                </Badge>
                              )}
                            </div>
                            <CardDescription>{reminder.message}</CardDescription>
                          </div>
                          <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(reminder.scheduledFor)}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {reminder.actionItems && reminder.actionItems.length > 0 && (
                            <div>
                              <h4 className="font-medium mb-2">Action Items:</h4>
                              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                {reminder.actionItems.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                {reminder.channels?.includes('email') && <Mail className="h-4 w-4 text-gray-500" />}
                                {reminder.channels?.includes('sms') && <MessageSquare className="h-4 w-4 text-gray-500" />}
                                {reminder.channels?.includes('app') && <Bell className="h-4 w-4 text-gray-500" />}
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {reminder.business?.name}
                              </span>
                            </div>
                            
                            {!reminder.isRead && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => markReminderRead(reminder.id)}
                              >
                                Mark as Read
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Preferences</CardTitle>
                  <CardDescription>
                    Configure how you'd like to receive tax deadline reminders
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="email-notifications">Email Notifications</Label>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Receive reminders via email
                        </div>
                      </div>
                      <Switch
                        id="email-notifications"
                        checked={preferences.emailEnabled}
                        onCheckedChange={(checked) => 
                          setPreferences(prev => ({ ...prev, emailEnabled: checked }))
                        }
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="sms-notifications">SMS Notifications</Label>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Receive urgent reminders via text message
                        </div>
                      </div>
                      <Switch
                        id="sms-notifications"
                        checked={preferences.smsEnabled}
                        onCheckedChange={(checked) => 
                          setPreferences(prev => ({ ...prev, smsEnabled: checked }))
                        }
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="app-notifications">In-App Notifications</Label>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Show reminders in the application
                        </div>
                      </div>
                      <Switch
                        id="app-notifications"
                        checked={preferences.appEnabled}
                        onCheckedChange={(checked) => 
                          setPreferences(prev => ({ ...prev, appEnabled: checked }))
                        }
                      />
                    </div>
                  </div>
                  
                  <Button onClick={updatePreferences}>
                    <Settings className="mr-2 h-4 w-4" />
                    Save Preferences
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Reminder Schedule</CardTitle>
                  <CardDescription>
                    Smart reminders are automatically scheduled based on deadline importance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Critical Deadlines</AlertTitle>
                      <AlertDescription>
                        Reminders sent 60, 30, 14, 7, 3, and 1 days before due date
                      </AlertDescription>
                    </Alert>
                    
                    <Alert>
                      <Clock className="h-4 w-4" />
                      <AlertTitle>High Priority</AlertTitle>
                      <AlertDescription>
                        Reminders sent 30, 14, 7, 3, and 1 days before due date
                      </AlertDescription>
                    </Alert>
                    
                    <Alert>
                      <Bell className="h-4 w-4" />
                      <AlertTitle>Medium Priority</AlertTitle>
                      <AlertDescription>
                        Reminders sent 14, 7, and 3 days before due date
                      </AlertDescription>
                    </Alert>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}